"use client";

import { Check, ArrowRight, Star } from "lucide-react";
import { BROKERAGE_PLANS, BROKING_SEGMENTS, formatINR } from "@/lib/demos/stockBrokingData";

interface StockBrokingServicesSectionProps {
  onSelectPlan: (planId: string) => void;
}

export function StockBrokingServicesSection({ onSelectPlan }: StockBrokingServicesSectionProps) {
  return (
    <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 text-xs font-semibold uppercase tracking-wider mb-3">
            Transparent Brokerage Plans
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Pick a plan that trades the way you do</h2>
          <p className="text-slate-500 text-sm sm:text-base mt-3">Flat, transparent pricing across every exchange. Free equity delivery and zero-commission direct mutual funds on every plan.</p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {BROKERAGE_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 sm:p-7 flex flex-col justify-between transition-all group ${plan.highlight ? "bg-gradient-to-b from-emerald-950/40 to-slate-900 border-2 border-emerald-500/40 shadow-xl shadow-emerald-950/30" : "bg-white border border-slate-200 hover:border-emerald-500/40"}`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-lg">
                  <Star className="w-3 h-3 fill-current" /> Most Popular
                </span>
              )}
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">{plan.badge}</span>
                  <span className="text-[11px] text-slate-500">AMC {formatINR(plan.amc)}/yr</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">{plan.title}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{plan.tagline}</p>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {[
                    { l: "Delivery", v: plan.deliveryBrokerage },
                    { l: "Intraday", v: plan.intradayBrokerage },
                    { l: "F&O", v: plan.fnoBrokerage },
                  ].map((x) => (
                    <div key={x.l} className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="text-[9px] text-slate-500 uppercase">{x.l}</div>
                      <div className="text-[11px] font-bold text-slate-900 mt-0.5 leading-tight">{x.v}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 space-y-2.5">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-xs text-slate-600">
                      <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-emerald-600" />
                      </div>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => onSelectPlan(plan.id)}
                className={`mt-6 w-full py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${plan.highlight ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20" : "bg-emerald-500/15 hover:bg-emerald-500 text-emerald-700 hover:text-white border border-emerald-500/30 hover:border-transparent"}`}
              >
                Open account on this plan <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Segment strip */}
        <div className="mt-14 rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 mb-4 text-center">One account · every segment</h3>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {BROKING_SEGMENTS.map((s) => (
              <span key={s.id} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${s.color}`}>
                {s.label} <span className="opacity-60">· {s.exchange}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
